// =============================================================================
// src/utils/queryInvalidation.ts
// -----------------------------------------------------------------------------
// Invalidación por DOMINIO: un mismo dato vive bajo varias query keys (lista +
// contador, editor + preview + viewer, detail + summary). Toda mutación debe
// invalidar el set completo vía estos helpers — invalidar una sola key a mano
// deja las hermanas stale 5 min (staleTime global) o hasta F5.
// =============================================================================

import type { QueryClient } from "@tanstack/react-query";

/** Reservas: lista + contador del sidebar + contadores por recurso (tour/venue). */
export function invalidateBookings(qc: QueryClient, ownerId?: string) {
  // Sin ownerId → prefijo (todas las cuentas); ["x", undefined] no matchearía nada.
  void qc.invalidateQueries({
    queryKey: ownerId ? ["bookings", ownerId] : ["bookings"],
  });
  void qc.invalidateQueries({
    queryKey: ownerId
      ? ["bookings-count-pending", ownerId]
      : ["bookings-count-pending"],
  });
  void qc.invalidateQueries({ queryKey: ["tour-booking-count"] });
  void qc.invalidateQueries({ queryKey: ["venue-booking-count"] });
}

/** Venue .tap: cache del editor + lista + cache del viewer por short_id + directorio público. */
export function invalidateTapVenue(qc: QueryClient, venueId?: string) {
  void qc.invalidateQueries({
    queryKey: venueId ? ["tap-venue", venueId] : ["tap-venue"],
  });
  void qc.invalidateQueries({ queryKey: ["tap-venues"] });
  void qc.invalidateQueries({ queryKey: ["tap-venue-slug"] });
  void qc.invalidateQueries({ queryKey: ["listed-venues"] });
}

/** Galería Multimedia del tour (uploads/deletes de escena, sala, piso, cover, branding). */
export function invalidateTourMedia(qc: QueryClient, tourId?: string) {
  void qc.invalidateQueries({
    queryKey: tourId ? ["tour", "media", tourId] : ["tour", "media"],
  });
}

/** Menú .tap: cache del editor (all, incluye ocultos) + cache público (viewer + phone preview). */
export function invalidateTapMenu(qc: QueryClient, branchId?: string | null) {
  void qc.invalidateQueries({ queryKey: ["tap-menu-all", branchId] });
  void qc.invalidateQueries({ queryKey: ["tap-menu", branchId] });
}

/** Botones .tap: lista del editor + activos (previews de tabs) + viewer + cards del grid. */
export function invalidateTapButtons(qc: QueryClient, venueId: string) {
  void qc.invalidateQueries({ queryKey: ["venue-action-buttons", venueId] });
  void qc.invalidateQueries({ queryKey: ["venue-action-buttons-active", venueId] });
  void qc.invalidateQueries({ queryKey: ["tap-buttons", venueId] });
  // Key con array de venueIds (TapDashboardPage) → invalidación por prefijo.
  void qc.invalidateQueries({ queryKey: ["tap-venue-buttons"] });
}

/** Admin users: AdminUserSummary duplica campos del detail (email, plan, demo, avatar). */
export function invalidateAdminUser(qc: QueryClient, userId?: string) {
  void qc.invalidateQueries({ queryKey: ["admin", "users", "summary"] });
  if (userId)
    void qc.invalidateQueries({ queryKey: ["admin", "users", "detail", userId] });
}
